import {
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TabPanel from '../tools/TabPanelv2';
import ListJobs from './ListJobs';
import MapComponent from './MapComponent';

export default function MappingView(props) {
  const {
    bucketMapLayout,
    mappingJobsList,
    loadingMappingJobs,
    availableStoresDict,
    availableFloorsList,
    setSnackAlert,
    getMappingJobs,
    getBucketMapLayout,
    getAvailableStores,
    getAvailableFloors,
    isLoadingBucketMapLayout,
    postMappingJob,
    postMappingJobResponse,
    isLoadingPostMappingJob,
    deleteMappingJob,
    deleteJobResponse,
    isLoadingDeleteJob,
    patchMappingJob,
    jobUpdateResponse,
    isLoadingJobUpdate,
    getRobotLayout,
    robotLayout,
    jobMapImg,
    isLoadingJobImg,
    getBucketMapJobImg,
  } = props;

  const { t } = useTranslation();

  const [tab, setTab] = useState(0);
  const [selectedJobs, setSelectedJobs] = useState([{}, {}, {}]);
  const [availableStoresList, setAvailableStoresList] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [creatingNewJob, setCreatingNewJob] = useState(false);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [inReviewJobs, setInReviewJobs] = useState([]);
  const [readyJobs, setReadyJobs] = useState([]);
  const [mode, setMode] = useState(null);
  const [selectedMaps, setSelectedMaps] = useState([{}, {}, {}]);
  const [mapMetadata, setMapMetadata] = useState(null);

  useEffect(() => {
    getMappingJobs();
    getAvailableStores();
    setMode('pending');
  }, []);

  useEffect(() => {
    if (robotLayout?.metadata) setMapMetadata(robotLayout?.metadata);
    else setMapMetadata(null);
  }, [robotLayout]);

  useEffect(() => {
    if (bucketMapLayout) {
      setSelectedMaps((prev) => {
        const newList = [...prev];
        newList[tab] = bucketMapLayout;
        return newList;
      });
    }
  }, [bucketMapLayout]);

  useEffect(() => {
    if (jobMapImg) {
      setSelectedMaps((prev) => {
        const newList = [...prev];
        newList[tab] = jobMapImg;
        return newList;
      });
      setMapMetadata(jobMapImg?.map_metadata);
    }
  }, [jobMapImg]);

  useEffect(() => {
    setPendingJobs(mappingJobsList.filter((item) => item.state === 'Pending'));
    setInReviewJobs(
      mappingJobsList.filter((item) => item.state === 'In Review')
    );
    setReadyJobs(
      mappingJobsList.filter((item) =>
        ['Accepted', 'Applied'].includes(item.state)
      )
    );
  }, [mappingJobsList]);

  useEffect(() => {
    if (postMappingJobResponse) getMappingJobs();
  }, [postMappingJobResponse]);

  useEffect(() => {
    if (deleteJobResponse) getMappingJobs();
  }, [deleteJobResponse]);

  useEffect(() => {
    if (jobUpdateResponse && !isLoadingJobUpdate) {
      getMappingJobs();
      setSelectedMaps((prev) => {
        const newList = [...prev];
        newList[tab] = {};
        return newList;
      });
    }
  }, [jobUpdateResponse, isLoadingJobUpdate]);

  useEffect(() => {
    setAvailableStoresList(availableStoresDict?.stores);
  }, [availableStoresDict]);

  useEffect(() => {
    if (creatingNewJob) {
      getBucketMapLayout({
        store: selectedStore.store_id,
        floor: selectedFloor?.value,
      });
      getRobotLayout({
        store: selectedStore.store_id,
        floor: selectedFloor?.value,
      });
    }
  }, [creatingNewJob]);

  useEffect(() => {
    const job = selectedJobs[tab];
    // check if have blob path
    let blob_path = job?.blob_path;
    if (blob_path) {
      getBucketMapJobImg(job.mapping_job_id);
    } else if (job?.store && job?.floor) {
      getBucketMapLayout({ store: job.store, floor: job.floor });
      getRobotLayout({ store: job.store, floor: job.floor });
    }
  }, [selectedJobs]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    switch (newValue) {
      case 0:
        setMode('pending');
        break;
      case 1:
        setMode('review');
        break;
      case 2:
        setMode('submit');
        break;
      default:
        setMode('pending');
        break;
    }
  };

  return (
    <Stack component={Paper} sx={{ minHeight: 'calc(100vh - 15em)' }}>
      <Tabs value={tab} onChange={handleTabChange}>
        <Tab label={t('overseer_app.mapping.pending_jobs', 'Pending Jobs')} />
        <Tab
          label={t('overseer_app.mapping.jobs_in_review', 'Jobs In Review')}
        />
        <Tab label={t('overseer_app.mapping.submit_map', 'Submit Map')} />
      </Tabs>
      <TabPanel value={tab} index={0} sx={{ p: 2, minHeight: 'inherit' }}>
        <Grid container height={'80vh'}>
          <Grid item xs={3}>
            {loadingMappingJobs ? (
              <LinearProgress
                sx={{ width: '95%', mx: 'auto' }}
                color="secondary"
              />
            ) : (
              <ListJobs
                tabIndex={tab}
                selectedJob={selectedJobs[tab]}
                setSelectedJobs={setSelectedJobs}
                mappingJobsList={mappingJobsList}
                jobs={pendingJobs}
                availableStores={availableStoresList}
                getAvailableFloors={getAvailableFloors}
                availableFloors={availableFloorsList}
                setCreatingNewJob={setCreatingNewJob}
                selectedStore={selectedStore}
                setSelectedStore={setSelectedStore}
                selectedFloor={selectedFloor?.value}
                setSelectedFloor={setSelectedFloor}
                deleteMappingJob={deleteMappingJob}
                getBucketMapLayout={getBucketMapLayout}
                isLoadingBucketMapLayout={isLoadingBucketMapLayout}
                isLoadingJobImg={isLoadingJobImg}
              />
            )}
          </Grid>
          <Grid item xs={9} style={{ position: 'relative' }}>
            <MapComponent
              canvasId={'pendingJobs'}
              tabIndex={tab}
              mode={mode}
              selectedJob={selectedJobs[tab]}
              setSelectedJobs={setSelectedJobs}
              mapInfo={selectedMaps[tab]}
              storeInfo={selectedStore}
              robotFloor={selectedFloor?.value}
              isLoadingBucketMapLayout={isLoadingBucketMapLayout}
              postMappingJob={postMappingJob}
              postMappingJobResponse={postMappingJobResponse}
              patchMappingJob={patchMappingJob}
              mapMetadata={mapMetadata}
              isLoadingJobImg={isLoadingJobImg}
            />
            {(isLoadingPostMappingJob || isLoadingJobUpdate) && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                }}
              >
                <CircularProgress color="inherit" />
              </div>
            )}
          </Grid>
        </Grid>
      </TabPanel>
      <TabPanel value={tab} index={1} sx={{ p: 2, minHeight: 'inherit' }}>
        <Grid container height={'80vh'}>
          <Grid item xs={3}>
            {loadingMappingJobs ? (
              <LinearProgress
                sx={{ width: '95%', mx: 'auto' }}
                color="secondary"
              />
            ) : (
              <ListJobs
                tabIndex={tab}
                selectedJob={selectedJobs[tab]}
                setSelectedJobs={setSelectedJobs}
                jobs={inReviewJobs}
                availableStores={availableStoresList}
                getAvailableFloors={getAvailableFloors}
                availableFloors={availableFloorsList}
                setCreatingNewJob={setCreatingNewJob}
                selectedStore={selectedStore}
                setSelectedStore={setSelectedStore}
                selectedFloor={selectedFloor?.value}
                setSelectedFloor={setSelectedFloor}
                deleteMappingJob={deleteMappingJob}
                getBucketMapLayout={getBucketMapLayout}
                isLoadingBucketMapLayout={isLoadingBucketMapLayout}
                isLoadingJobImg={isLoadingJobImg}
              />
            )}
          </Grid>
          <Grid item xs={9} style={{ position: 'relative' }}>
            <MapComponent
              canvasId={'inReviewJobs'}
              tabIndex={tab}
              mode={mode}
              selectedJob={selectedJobs[tab]}
              setSelectedJobs={setSelectedJobs}
              mapInfo={selectedMaps[tab]}
              storeInfo={selectedStore}
              robotFloor={selectedFloor?.value}
              isLoadingBucketMapLayout={isLoadingBucketMapLayout}
              postMappingJob={postMappingJob}
              postMappingJobResponse={postMappingJobResponse}
              patchMappingJob={patchMappingJob}
              mapMetadata={mapMetadata}
              isLoadingJobImg={isLoadingJobImg}
            />
            {(isLoadingPostMappingJob || isLoadingJobUpdate) && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                }}
              >
                <CircularProgress color="inherit" />
              </div>
            )}
          </Grid>
        </Grid>
      </TabPanel>
      <TabPanel value={tab} index={2} sx={{ p: 2, minHeight: 'inherit' }}>
        <Grid container height={'80vh'}>
          <Grid item xs={3}>
            {loadingMappingJobs ? (
              <LinearProgress
                sx={{ width: '95%', mx: 'auto' }}
                color="secondary"
              />
            ) : (
              <ListJobs
                tabIndex={tab}
                selectedJob={selectedJobs[tab]}
                setSelectedJobs={setSelectedJobs}
                jobs={readyJobs}
                availableStores={availableStoresList}
                getAvailableFloors={getAvailableFloors}
                availableFloors={availableFloorsList}
                setCreatingNewJob={setCreatingNewJob}
                selectedStore={selectedStore}
                setSelectedStore={setSelectedStore}
                selectedFloor={selectedFloor?.value}
                setSelectedFloor={setSelectedFloor}
                deleteMappingJob={deleteMappingJob}
                getBucketMapLayout={getBucketMapLayout}
                isLoadingBucketMapLayout={isLoadingBucketMapLayout}
                isLoadingJobImg={isLoadingJobImg}
              />
            )}
          </Grid>
          <Grid item xs={9} style={{ position: 'relative' }}>
            <MapComponent
              canvasId={'submitJob'}
              tabIndex={tab}
              mode={mode}
              selectedJob={selectedJobs[tab]}
              setSelectedJobs={setSelectedJobs}
              mapInfo={selectedMaps[tab]}
              storeInfo={selectedStore}
              robotFloor={selectedFloor?.value}
              isLoadingBucketMapLayout={isLoadingBucketMapLayout}
              postMappingJob={postMappingJob}
              postMappingJobResponse={postMappingJobResponse}
              patchMappingJob={patchMappingJob}
              mapMetadata={mapMetadata}
              isLoadingJobImg={isLoadingJobImg}
            />
            {(isLoadingPostMappingJob || isLoadingJobUpdate) && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                }}
              >
                <CircularProgress color="inherit" />
              </div>
            )}
          </Grid>
        </Grid>
      </TabPanel>
    </Stack>
  );
}
